import { useTenant } from '@emerald-works-nova/auth'
import { useMemo } from 'react'
import { ALLOW_REPORTING_GROUP } from '../components/user-group-policy/permissions'
import consts from '../consts'
import useGroup from './useGroup'

const useProducts = () => {
  const tenant = useTenant()
  const isClub = tenant.pk === consts.DEFAULT_CLUB_PK
  const { isInGroup } = useGroup()

  const hasContentHubAccess = useMemo(() => {
    if (isClub) return true

    const hasContentHubField = Object.hasOwn(tenant.features, 'contentHub')
    const { contentHub } = tenant.features
    return hasContentHubField ? contentHub : true
  }, [tenant, isClub])

  const hasMsaAccess = useMemo(() => {
    return tenant.features?.assessment?.enabled ?? tenant.features?.msa ?? false
  }, [tenant])

  const hasMsbAccess = useMemo(() => {
    return tenant.features?.msb ?? false
  }, [tenant])

  const hasSkillBitesAccess = useMemo(() => {
    return tenant.features?.skillBite?.enabled
  }, [tenant])

  const isSkillBitesFlexibleSchedulingEnabled = useMemo(() => {
    return tenant.features?.skillBite?.flexibleScheduling ?? false
  }, [tenant])

  const isCredlyBadgesEnabled = useMemo(() => {
    return tenant.features?.skillBite?.credlyBadges ?? false
  }, [tenant])

  const hasSkillsPracticeAccess = useMemo(() => {
    if (isClub) return false
    return tenant.features?.skillsPractice?.enabled === true
  }, [tenant, isClub])

  const msaActiveAssessmentId = tenant.features?.assessment?.activeAssessmentId ?? 'manager-skill-assessment'

  const hasLPQAccess = useMemo(() => {
    return tenant.features?.lpq?.enabled ?? true
  }, [tenant])

  const showLPQModalPopup = useMemo(() => {
    return tenant.features?.lpq?.showModalPopup ?? true
  }, [tenant])

  const lpqMandatory = useMemo(() => {
    return !(tenant.features?.lpq?.isOptional ?? true)
  }, [tenant])

  // PENG-3783: single source of truth for the playlists flag — six call sites read it and the
  // tenant key was renamed, so a site left on the legacy read reports playlists as disabled for
  // every tenant. The `?? features.playlists` fallback covers tenants the
  // CHANGE_PLAYLISTS_STRUCTURE patch has not reached yet; drop it once all envs are migrated.
  //
  // Deliberately no `|| isClub` here: four of the six sites bypass the flag for the club tenant
  // and two (my-learning/learning-activity, accelerate/completion-state) deliberately do not.
  // Each call site keeps its own club handling so this refactor is behaviour-neutral.
  const hasPlaylistsAccess = useMemo(() => {
    return tenant.features?.playlist?.enabled ?? tenant.features?.playlists ?? false
  }, [tenant])

  const adminOnlyPlaylistThumbnailUpload = useMemo(() => {
    if (isClub) return false
    return tenant.features?.playlist?.adminOnlyThumbnailUpload ?? false
  }, [tenant, isClub])

  const hasReportingAccess = useMemo(() => {
    return (hasContentHubAccess || hasSkillBitesAccess) && isInGroup(ALLOW_REPORTING_GROUP)
  }, [hasContentHubAccess, hasSkillBitesAccess, isInGroup])

  return {
    hasContentHubAccess,
    hasMsaAccess,
    hasMsbAccess,
    hasSkillBitesAccess,
    hasSkillsPracticeAccess,
    hasReportingAccess,
    hasMsaAssessmentAccess: (assessmentId) => msaActiveAssessmentId === assessmentId,
    msaActiveAssessmentId,
    isSkillBitesFlexibleSchedulingEnabled,
    isCredlyBadgesEnabled,
    hasLPQAccess,
    showLPQModalPopup,
    lpqMandatory,
    hasPlaylistsAccess,
    adminOnlyPlaylistThumbnailUpload
  }
}

export default useProducts
